import React, { useState } from 'react';
import { InvitationType } from '../../_graphql/generated/types';

interface InvitationTypeSelectionModalProps {
  isOpen: boolean;
  invitationTypes: InvitationType[];
  onSelect: (invitationTypeId: string) => void;
  onCancel: () => void;
  isCreating?: boolean;
}

const InvitationTypeSelectionModal: React.FC<InvitationTypeSelectionModalProps> = ({
  isOpen,
  invitationTypes,
  onSelect,
  onCancel,
  isCreating = false
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTypeId) {
      onSelect(selectedTypeId);
    }
  };

  // Filter to only show invitation types with designs
  const typesWithDesigns = invitationTypes.filter(type => 
    type.designCustomizations !== null && type.designCustomizations !== undefined
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-rose-100">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold bg-gradient-to-r from-rose-400 to-pink-600 bg-clip-text text-transparent">
            Select Invitation Type
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
            disabled={isCreating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Choose which invitation type to use for this invitation:
            </p>
            
            {typesWithDesigns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg 
                  className="mx-auto h-12 w-12 text-gray-400 mb-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">No invitation types with designs</p>
                <p className="text-sm text-gray-500">Please design at least one invitation type first.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {typesWithDesigns.map((type) => (
                  <label
                    key={type.id}
                    className={`block p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedTypeId === type.id
                        ? 'border-rose-400 bg-rose-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="invitationType"
                      value={type.id}
                      checked={selectedTypeId === type.id}
                      onChange={(e) => setSelectedTypeId(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{type.name}</h3>
                        {type.description && (
                          <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                        )}
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ml-3 ${
                        selectedTypeId === type.id
                          ? 'border-rose-400'
                          : 'border-gray-300'
                      }`}>
                        {selectedTypeId === type.id && (
                          <div className="w-3 h-3 rounded-full bg-rose-400" />
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {typesWithDesigns.length > 0 && (
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={!selectedTypeId || isCreating}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-full hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isCreating ? 'Creating...' : 'Create Invitation'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={isCreating}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default InvitationTypeSelectionModal;